import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useToast } from '../../context/ToastContext';
import ApiService, { handleApiError } from '../../services/api';
import { useScenarioContext, scenarioActions, FAVORITES_UI_CACHE_KEY } from '../../context/ScenarioContext';
import { FavoriteScenario, ScenarioResult, PayerMixItem, ProductFamily, RebateDetail } from '../../types';
import RebateDetailDialog, { RebateDetailTarget } from '../../components/RebateDetailDialog/RebateDetailDialog';
import { useExportScenarios } from '../../hooks/useExportScenarios';
import { usePracticeData } from '../../hooks/usePracticeData';
import {
  BreakdownTable, Card, Field, FilterBand, Modal, PageHeader, PayerAssignmentTable, PayerEligibility, Tip,
  alignEnd, familyLabel, headerEnd, notify, orUndefined, sortLineFor, sortableAs, tableView, timeOf,
} from '../../components/ui';
import { money, shortDate, unitStrength } from '../../utils/format';
import { buildFamilyColors, familyColor, sortByFamily } from '../../utils/families';
import { AppTable, AppTableHorizontalScrollControls, Button, Dropdown, Menu, SegmentedControl, StatusTag, Tooltip, useAppTableHorizontalScroll, type MenuRef } from '@allygpo/ally-ux';
import { getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, type ColumnDef, type ExpandedState, type SortingState } from '@tanstack/react-table';

// Generate signature for a complete scenario (all product families together).
// Must match ScenarioResults.getScenarioSignature, which keys the Results star cache.
const getScenarioSignature = (scenarioDetails: ScenarioResult[]) => {
  // Sort by product_family_id to ensure consistent ordering
  const sorted = [...scenarioDetails].sort((a, b) =>
    String(a.product_family_id).localeCompare(String(b.product_family_id))
  );

  // Create a signature from the distribution of each product family
  const signature = {
    logged_in_user: sorted[0]?.logged_in_user,
    category: sorted[0]?.category,
    distributions: sorted.map(detail => ({
      product_family_id: detail.product_family_id,
      future_distribution: detail.future_distribution,
      invoice_units: detail.invoice_units
    }))
  };
  return JSON.stringify(signature);
};

// Keep the ScenarioResults star cache in step when a favorite is removed here.
const removeFromScenarioResultsCache = (scenarioDetails: ScenarioResult[]) => {
  try {
    const cached = localStorage.getItem(FAVORITES_UI_CACHE_KEY);
    if (!cached) return;
    const cachedFavorites = new Set<string>(JSON.parse(cached) as string[]);
    cachedFavorites.delete(getScenarioSignature(scenarioDetails));
    localStorage.setItem(FAVORITES_UI_CACHE_KEY, JSON.stringify(Array.from(cachedFavorites)));
  } catch (error) {
    console.error('Failed to update ScenarioResults cache');
  }
};

type FavoriteRow = FavoriteScenario & {
  _totals: { totalNCR: number; totalWeightedNCR: number; totalPrice: number; totalRebate: number; totalMedicareReimb: number; totalWeightedReimb: number };
  /** Reimbursement and NCR for the selected metric (Medicare or weighted). */
  _reimb: number;
  _ncr: number;
};
const FAVORITES_PAGE_SIZE = 10;

/** What the Favorites table cells read from `meta.view`. */
interface FavoritesView {
  metricName: string;
  isDemoMode: boolean;
  updatingFavoriteId: number | null;
  removeFavorite: (fav: FavoriteScenario) => void;
}

const favoriteColumns: ColumnDef<FavoriteRow>[] = [
  {
    // Star and expand chevron share one cell so they sit together.
    id: 'controls',
    size: 88,
    enableSorting: false,
    header: '',
    cell: ({ row, table }) => {
      const { original: fav } = row;
      const { isDemoMode, updatingFavoriteId, removeFavorite } = tableView<FavoritesView, FavoriteRow>(table);
      const open = row.getIsExpanded();
      // Per-row class so each Tooltip binds to its own star rather than to every star in the table.
      const demoTipClass = `favorite-star-demo-${fav.id}`;
      return <div className="flex align-items-center">
        {isDemoMode && <Tooltip target={`.${demoTipClass}`} position="top" />}
        {updatingFavoriteId === fav.id ? <i className="pi pi-spin pi-spinner text-tertiary" aria-label="Updating" /> : <Button
          variant="secondary-text"
          className={`text-warning${isDemoMode ? ` opacity-50 ${demoTipClass}` : ''}`}
          aria-label={`Remove ${fav.category} favorite`}
          data-pr-tooltip={isDemoMode ? 'Read-only demo account' : undefined}
          onClick={() => { if (!isDemoMode) removeFavorite(fav); }}
        >
          <i className="pi pi-star-fill" aria-hidden="true" />
        </Button>}
        <Button variant="secondary-text" aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${fav.category}`} onClick={row.getToggleExpandedHandler()}>
          <i className={`pi ${open ? 'pi-chevron-down' : 'pi-chevron-right'}`} aria-hidden="true" />
        </Button>
      </div>;
    },
  },
  {
    id: 'cta',
    accessorFn: (row) => orUndefined(row.category),
    ...sortableAs('text'),
    size: 170,
    header: () => <Tip text="Competitive Therapeutic Area">Therapeutic area</Tip>,
    cell: ({ row: { original: row } }) => (
      <span className="fav-cta flex align-items-center u-gap-2 flex-wrap font-medium">
        {row.category}
        {row.is_expired && <Tip text="Saved in an earlier quarter, so its figures use that quarter's pricing and volumes."><StatusTag variant="warning">Expired</StatusTag></Tip>}
      </span>
    ),
  },
  {
    id: 'reimb',
    accessorFn: (row) => row._reimb,
    ...sortableAs('number'),
    size: 100,
    header: ({ table }) => headerEnd(`${tableView<FavoritesView, FavoriteRow>(table).metricName} reimb.`),
    cell: ({ getValue }) => money(getValue<number>()),
    meta: alignEnd,
  },
  {
    id: 'price',
    accessorFn: (row) => row._totals.totalPrice,
    ...sortableAs('number'),
    size: 90,
    header: () => headerEnd('Price'),
    cell: ({ getValue }) => money(getValue<number>()),
    meta: alignEnd,
  },
  {
    id: 'rebate',
    accessorFn: (row) => row._totals.totalRebate,
    ...sortableAs('number'),
    size: 90,
    header: () => headerEnd('Rebate'),
    cell: ({ getValue }) => money(getValue<number>()),
    meta: alignEnd,
  },
  {
    id: 'ncr',
    accessorFn: (row) => row._ncr,
    ...sortableAs('number'),
    size: 100,
    header: ({ table }) => headerEnd(`${tableView<FavoritesView, FavoriteRow>(table).metricName} NCR`),
    cell: ({ getValue }) => <span className={`fav-ncr font-semibold${getValue<number>() < 0 ? ' siq-neg text-danger' : ''}`}>{money(getValue<number>())}</span>,
    meta: alignEnd,
  },
  {
    id: 'user',
    accessorFn: (row) => orUndefined(row.created_user),
    ...sortableAs('text'),
    size: 110,
    header: 'Created by',
    cell: ({ row: { original: row }, table }) => (
      <span className="fav-muted text-secondary overflow-hidden text-overflow-ellipsis white-space-nowrap">{tableView<FavoritesView, FavoriteRow>(table).isDemoMode ? 'Demo User' : row.created_user}</span>
    ),
  },
  {
    id: 'date',
    accessorFn: (row) => timeOf(row.created_date),
    ...sortableAs('date'),
    size: 90,
    header: 'Saved',
    cell: ({ row: { original: row } }) => <span className="fav-muted text-secondary overflow-hidden text-overflow-ellipsis white-space-nowrap">{shortDate(row.created_date)}</span>,
  },
];

/** Dropdown value for the "All …" options; the filters themselves store '' for no filter. */
const ALL = '__all__';

const FavoriteScenarios: React.FC = () => {
  const { state, dispatch } = useScenarioContext();
  // The app-wide toast (Layout), so messages survive navigation, e.g. generation finishing after Results opens.
  const toast = useToast();
  const exportMenuRef = useRef<MenuRef>(null);
  const [favorites, setFavorites] = useState<FavoriteScenario[]>([]);
  const [loading, setLoading] = useState(false);
  const latestFavoritesRequestRef = useRef<string | null>(null);
  // Nothing is expanded on first load.
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  const [ncrMode, setNcrMode] = useState<'medicare' | 'weighted'>('medicare');
  // Toggle between per-category-unit (EU) and per-invoice-unit (IU) figures. Always opens on category.
  const [unitMode, setUnitMode] = useState<'category' | 'invoice'>('category');
  // Oral and non-oral favorites are shown in separate views, never interleaved.
  const [inventoryMode, setInventoryMode] = useState<'nonoral' | 'oral'>('nonoral');
  // Oral categories use benchmark (not Medicare) reimbursement terminology.
  const ncrLabel = inventoryMode === 'oral' ? 'Benchmark' : 'Medicare';
  const [payerMixAvailable, setPayerMixAvailable] = useState<boolean>(false);
  // True once the payer-mix fetch has resolved (any outcome). The Metric and Units filters
  // are gated on this so they appear together instead of one rendering ahead of the other.
  const [payerMixLoaded, setPayerMixLoaded] = useState<boolean>(false);
  // Payer mix per area, for the payer-assignments table (each payer's share is per area). The page-level
  // fetch below seeds the first area and decides whether the weighted metric is offered.
  const [payerMixByCategory, setPayerMixByCategory] = useState<Record<string, PayerMixItem[]>>({});

  // Payer assignments modal state
  const [isDecisionLogicModalVisible, setDecisionLogicModalVisible] = useState(false);
  const [decisionLogicScenario, setDecisionLogicScenario] = useState<{
    scenario_id: number;
    category: string;
    payerAssignments?: Record<string, string>;
    details: ScenarioResult[];
  } | null>(null);
  const [expandedDecisionPayers, setExpandedDecisionPayers] = useState<Record<number, Record<string, boolean>>>({});

  // Rebate drill-down dialog state, same shape as ScenarioResults. The favorite is kept
  // alongside the target because its saved rebate_details and scenario_details rows are
  // what the dialog renders — there is no context state to fall back on here.
  const [rebateDetailContext, setRebateDetailContext] = useState<{
    favorite: FavoriteScenario;
    target: RebateDetailTarget;
  } | null>(null);
  // Separate from the target so closing can animate out with its content still mounted.
  const [rebateDetailVisible, setRebateDetailVisible] = useState(false);
  const rebateDetailRows = useMemo(() => {
    if (!rebateDetailContext) return [];
    const saved = rebateDetailContext.favorite.rebate_details ?? [];
    return saved.filter(d => String(d.product_family_id) === String(rebateDetailContext.target.pfId));
  }, [rebateDetailContext]);
  const rebateDetailSummaryRow = useMemo(() => {
    if (!rebateDetailContext) return undefined;
    return (rebateDetailContext.favorite.scenario_details || []).find(
      r => String(r.product_family_id) === String(rebateDetailContext.target.pfId)
    );
  }, [rebateDetailContext]);

  // Payer eligibility + product families (cached per category)
  const [drugPayerMatrixByCategory, setDrugPayerMatrixByCategory] = useState<Record<string, Record<string, Record<string, boolean>>>>({});
  const [payerPrefsLoading, setPayerPrefsLoading] = useState(false);
  const [payerPrefsError, setPayerPrefsError] = useState<string | null>(null);
  const [productFamiliesByCategory, setProductFamiliesByCategory] = useState<Record<string, ProductFamily[]>>({});
  const [productFamiliesLoading, setProductFamiliesLoading] = useState(false);
  const [productFamiliesError, setProductFamiliesError] = useState<string | null>(null);
  const requestedFamilyCategoriesRef = useRef<Set<string>>(new Set());
  const [updatingFavoriteId, setUpdatingFavoriteId] = useState<number | null>(null);
  const { isDemoMode, practiceAccountNumbers } = usePracticeData();
  // Filters: '' means all. Exact matches only.
  const [filterUser, setFilterUser] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Export hook
  const { isExporting, handleExport } = useExportScenarios({ filenamePrefix: 'favorite_scenarios', toastRef: toast });

  // The practice is chosen in the top bar. Results may have pointed currentUser at the account
  // its run belongs to, so follow the top-bar selection here, as the page did before.
  const topBarAccount = state.generatorState.form?.logged_in_user;
  useEffect(() => {
    if (topBarAccount && topBarAccount !== state.currentUser) {
      dispatch(scenarioActions.setCurrentUser(topBarAccount));
    }
  }, [topBarAccount, state.currentUser, dispatch]);

  // Favorites for the active inventory view; oral vs non-oral are never shown together.
  const modeFavorites = useMemo(
    () => favorites.filter(f => Boolean(f.is_oral) === (inventoryMode === 'oral')),
    [favorites, inventoryMode]
  );

  const hasOral = useMemo(() => favorites.some(f => Boolean(f.is_oral)), [favorites]);
  const hasNonOral = useMemo(() => favorites.some(f => !f.is_oral), [favorites]);

  // If only one inventory type exists (or the current view is empty), snap to the one that has data.
  useEffect(() => {
    if (favorites.length === 0) return;
    if (inventoryMode === 'oral' && !hasOral && hasNonOral) setInventoryMode('nonoral');
    else if (inventoryMode === 'nonoral' && !hasNonOral && hasOral) setInventoryMode('oral');
  }, [favorites.length, hasOral, hasNonOral, inventoryMode]);

  // Each filter's options narrow to what the other filter leaves.
  const userOptions = useMemo(() => {
    const users = new Set(modeFavorites.filter(f => !filterCategory || f.category === filterCategory).map(f => f.created_user).filter(Boolean));
    return Array.from(users).sort((a, b) => a.localeCompare(b));
  }, [modeFavorites, filterCategory]);
  const categoryFilterOptions = useMemo(() => {
    const cats = new Set(modeFavorites.filter(f => !filterUser || f.created_user === filterUser).map(f => f.category).filter(Boolean));
    return Array.from(cats).sort((a, b) => a.localeCompare(b));
  }, [modeFavorites, filterUser]);

  const filteredFavorites = useMemo(() => modeFavorites.filter(fav =>
    (!filterUser || fav.created_user === filterUser) && (!filterCategory || fav.category === filterCategory)
  ), [modeFavorites, filterUser, filterCategory]);

  const rows: FavoriteRow[] = useMemo(() => filteredFavorites.map(f => {
    const details = f.scenario_details || [];
    const sum = (pick: (d: ScenarioResult) => number | undefined | null) => details.reduce((s, d) => s + Number(pick(d) || 0), 0);
    const totals = {
        totalNCR: sum(d => d.total_estimated_ncr),
        totalWeightedNCR: sum(d => d.total_weighted_ncr),
        totalPrice: sum(d => d.net_price),
        totalRebate: sum(d => d.total_rebate),
        totalMedicareReimb: sum(d => d.estimated_medicare_reimbursement),
        totalWeightedReimb: sum(d => d.estimated_pvt_reimbursement),
    };
    return {
      ...f,
      _totals: totals,
      _reimb: ncrMode === 'medicare' ? totals.totalMedicareReimb : totals.totalWeightedReimb,
      _ncr: ncrMode === 'medicare' ? totals.totalNCR : totals.totalWeightedNCR,
    };
  }), [filteredFavorites, ncrMode]);

  const metricName = ncrMode === 'medicare' ? ncrLabel : 'Weighted';
  // Labels for the "Sorted by …" line under the table, by column id.
  const sortLabels = useMemo(() => ({
    cta: { label: 'therapeutic area', kind: 'text' as const },
    reimb: { label: `${metricName} reimb.`, kind: 'number' as const },
    price: { label: 'price', kind: 'number' as const },
    rebate: { label: 'rebate', kind: 'number' as const },
    ncr: { label: `${metricName} NCR`, kind: 'number' as const },
    user: { label: 'created by', kind: 'text' as const },
    date: { label: 'date saved', kind: 'date' as const },
  }), [metricName]);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'date', desc: true }]);

  const filtersActive = Boolean(filterUser || filterCategory);
  const clearFilters = () => { setFilterUser(''); setFilterCategory(''); };

  const loadFavorites = useCallback(async () => {
    if (!state.currentUser) return;
    const requestedUser = state.currentUser;
    latestFavoritesRequestRef.current = requestedUser;
    setLoading(true);
    try {
      const response = await ApiService.getFavoriteScenarios(requestedUser);
      // Discard stale response if user changed while request was in-flight
      if (latestFavoritesRequestRef.current !== requestedUser) return;
      if (response.success && response.favorites) {
        // Filter only active favorites
        const activeFavorites = response.favorites.filter(f => f.is_active);
        // For demo users, only show favorites from allowed practice accounts
        const filtered = isDemoMode
          ? activeFavorites.filter(f => practiceAccountNumbers.includes(f.logged_in_user))
          : activeFavorites;
        setFavorites(filtered);
      }
    } catch (error) {
      // Don't show error toast for stale requests
      if (latestFavoritesRequestRef.current !== requestedUser) return;
      console.error('Failed to load favorites:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load favorites',
        life: 3000,
      });
    } finally {
      if (latestFavoritesRequestRef.current === requestedUser) {
        setLoading(false);
      }
    }
  }, [state.currentUser, isDemoMode, practiceAccountNumbers, toast]);

  useEffect(() => {
    if (state.currentUser) {
      setExpandedRows({});
      loadFavorites();
    }
  }, [state.currentUser, loadFavorites]);

  // Refetch payer mix using category endpoint; hide the weighted metric and force medicare if unavailable.
  // Depends on favorites so we always have a valid category to query against.
  useEffect(() => {
    const fetchMix = async () => {
      // Only non-oral categories carry payer mix; oral never offers the weighted metric.
      const firstCategory = favorites.find(f => f.category && !f.is_oral)?.category;
      if (!state.currentUser || !firstCategory) {
        setPayerMixAvailable(false);
        setNcrMode('medicare');
        setPayerMixLoaded(true);
        return;
      }
      try {
        const resp = await ApiService.getPayerMixByCategory({ logged_in_user: state.currentUser, category: firstCategory });
        const mix = ((resp as any)?.mix ?? []) as PayerMixItem[];
        const available = Array.isArray(mix) && mix.length > 0;
        setPayerMixAvailable(available);
        setPayerMixByCategory((prev) => ({ ...prev, [firstCategory]: available ? mix : [] }));
        if (!available) setNcrMode('medicare');
      } catch {
        setPayerMixAvailable(false);
        setNcrMode('medicare');
      } finally {
        setPayerMixLoaded(true);
      }
    };
    setPayerMixLoaded(false);
    fetchMix();
  }, [state.currentUser, favorites]);

  const normalizePayerName = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');

  // The open payer-assignments area's mix: each payer's share of that area.
  const modalPayerMix = useMemo(
    () => (decisionLogicScenario?.category ? payerMixByCategory[decisionLogicScenario.category] ?? [] : []),
    [payerMixByCategory, decisionLogicScenario?.category],
  );
  const payerMixByName = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of modalPayerMix) {
      if (!item?.payer) continue;
      map[normalizePayerName(item.payer)] = Number(item.percent ?? 0);
    }
    return map;
  }, [modalPayerMix]);

  const formatPfLabel = (name: string) => name ? name.charAt(0).toUpperCase() + name.slice(1) : '—';

  // Product families per category: the modal's family list, and the QTD shares that give each
  // family the same colour it has on Results. Fetched once per category.
  const ensureFamilies = useCallback(async (category: string) => {
    const userId = state.currentUser;
    if (!userId || !category || requestedFamilyCategoriesRef.current.has(category)) return;
    requestedFamilyCategoriesRef.current.add(category);
    setProductFamiliesLoading(true);
    setProductFamiliesError(null);
    try {
      const resp = await ApiService.getCategoryProductFamiliesNames(userId, category);
      const families = (resp as any)?.data?.product_families ?? (resp as any)?.product_families ?? [];
      setProductFamiliesByCategory((prev) => ({ ...prev, [category]: families as ProductFamily[] }));
    } catch (e) {
      requestedFamilyCategoriesRef.current.delete(category);
      setProductFamiliesError(handleApiError(e));
    } finally {
      setProductFamiliesLoading(false);
    }
  }, [state.currentUser]);

  useEffect(() => {
    requestedFamilyCategoriesRef.current = new Set();
    setProductFamiliesByCategory({});
  }, [state.currentUser]);

  useEffect(() => {
    favorites.filter(f => expandedRows[f.id]).forEach(f => { void ensureFamilies(f.category); });
  }, [expandedRows, favorites, ensureFamilies]);

  const colorsFor = useCallback((category: string, details: ScenarioResult[]) => {
    const listed = (productFamiliesByCategory[category] ?? []).map(pf => ({ name: pf.product_family_name || '', share: pf.product_family_to_category_qtd_ratio ?? 0 }));
    const known = new Set(listed.map(f => f.name.trim().toLowerCase()));
    const extra = details.map(d => d.product_family_name || '').filter(n => n && !known.has(n.trim().toLowerCase())).map(name => ({ name, share: null }));
    return buildFamilyColors([...listed, ...extra]);
  }, [productFamiliesByCategory]);

  const payerAssignmentsToRows = (
    payerAssignments: Record<string, string> | undefined,
    productFamilyNameById: Record<string, string>,
    payerMixLookupByName: Record<string, number>
  ) => {
    if (!payerAssignments) {
      return [] as Array<{ payer: string; payerPercent?: number; productFamilyId: string; productFamilyName: string }>;
    }

    return Object.entries(payerAssignments)
      .map(([payer, assignedId]) => {
        const productFamilyId = String(assignedId);
        const productFamilyName = productFamilyNameById[productFamilyId] ?? productFamilyId;
        const payerPercent = payerMixLookupByName[normalizePayerName(payer)];
        return { payer, payerPercent, productFamilyId, productFamilyName };
      })
      .sort((a, b) => {
        const ap = typeof a.payerPercent === 'number' && !Number.isNaN(a.payerPercent) ? a.payerPercent : undefined;
        const bp = typeof b.payerPercent === 'number' && !Number.isNaN(b.payerPercent) ? b.payerPercent : undefined;

        if (ap === undefined && bp !== undefined) return 1;
        if (ap !== undefined && bp === undefined) return -1;
        if (ap !== undefined && bp !== undefined && bp !== ap) return bp - ap;

        return a.payer.localeCompare(b.payer);
      });
  };

  const getPayerAssignmentsForFavorite = (fav: FavoriteScenario): Record<string, string> | undefined => {
    const direct = (fav as any)?.payer_assignments ?? (fav as any)?.payerAssignments;
    if (direct && typeof direct === 'object') return direct as Record<string, string>;

    const firstDetail = (fav.scenario_details?.[0] as any) ?? undefined;
    const fromDetail = firstDetail?.payer_assignments ?? firstDetail?.payerAssignments;
    if (fromDetail && typeof fromDetail === 'object') return fromDetail as Record<string, string>;
    return undefined;
  };

  // Flatten all scenario details for export, enriched with payer assignments (payer -> PF name)
  const exportableData = useMemo(() => {
    return filteredFavorites.flatMap((fav) => {
      const details = fav.scenario_details || [];
      const payerAssignments = getPayerAssignmentsForFavorite(fav);
      if (!payerAssignments || Object.keys(payerAssignments).length === 0) return details;

      const productFamilyNameById: Record<string, string> = {};
      for (const row of details) {
        const pfId = String((row as any)?.product_family_id ?? '');
        const pfName = String((row as any)?.product_family_name ?? '');
        if (!pfId || !pfName) continue;
        if (!productFamilyNameById[pfId]) productFamilyNameById[pfId] = pfName;
      }

      const payerAssignmentsNamed: Record<string, string> = {};
      for (const [payer, pfIdRaw] of Object.entries(payerAssignments)) {
        const pfId = String(pfIdRaw);
        payerAssignmentsNamed[payer] = productFamilyNameById[pfId] ?? pfId;
      }

      return details.map((row) => ({
        ...(row as any),
        payer_assignments: payerAssignmentsNamed,
      }) as ScenarioResult);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredFavorites]);

  // The saved rebate drill-down rows of the exported favorites, for the export's rebate sheet.
  const exportableRebateDetails = useMemo(
    () => filteredFavorites.flatMap((fav) => fav.rebate_details ?? []),
    [filteredFavorites]
  );

  const openDecisionLogicModal = (fav: FavoriteScenario) => {
    const details = fav.scenario_details || [];
    const scenarioId = Number(details?.[0]?.scenario_id);
    const category = String(fav.category || details?.[0]?.category || '');
    const payerAssignments = getPayerAssignmentsForFavorite(fav);

    if (!scenarioId || !category) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Missing data',
        detail: 'Unable to open decision logic for this favorite.',
        life: 3000,
      });
      return;
    }

    setDecisionLogicScenario({ scenario_id: scenarioId, category, payerAssignments, details });
    setDecisionLogicModalVisible(true);
  };

  const closeDecisionLogicModal = () => setDecisionLogicModalVisible(false);

  // Fetch payer preferences + product families for the modal category
  useEffect(() => {
    const userId = state.currentUser;
    const category = decisionLogicScenario?.category;
    if (!userId || !category || !isDecisionLogicModalVisible) return;

    let cancelled = false;

    const run = async () => {
      // Payer preferences (drug payer matrix)
      if (!drugPayerMatrixByCategory[category]) {
        setPayerPrefsLoading(true);
        setPayerPrefsError(null);
        try {
          const resp = await ApiService.getDrugPayerMatrix(userId, category);
          const matrix = (resp as any)?.drugPayerMatrix || (resp as any)?.preferences || {};
          if (!cancelled) {
            setDrugPayerMatrixByCategory((prev) => ({ ...prev, [category]: matrix }));
          }
        } catch (e) {
          if (!cancelled) {
            setDrugPayerMatrixByCategory((prev) => ({ ...prev, [category]: {} }));
            setPayerPrefsError(handleApiError(e));
          }
        } finally {
          if (!cancelled) setPayerPrefsLoading(false);
        }
      }
      // Payer mix for this area, so each payer's share is this area's, not the page's first area's.
      if (!(category in payerMixByCategory)) {
        try {
          const resp = await ApiService.getPayerMixByCategory({ logged_in_user: userId, category });
          const mix = ((resp as any)?.mix ?? []) as PayerMixItem[];
          if (!cancelled) setPayerMixByCategory((prev) => ({ ...prev, [category]: Array.isArray(mix) ? mix : [] }));
        } catch {
          if (!cancelled) setPayerMixByCategory((prev) => ({ ...prev, [category]: [] }));
        }
      }
      void ensureFamilies(category);
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentUser, decisionLogicScenario?.category, isDecisionLogicModalVisible]);

  const drugPayerMatrixByNormalizedPayer = useMemo(() => {
    const category = decisionLogicScenario?.category;
    const matrix = category ? (drugPayerMatrixByCategory[category] ?? {}) : {};

    const out: Record<string, Record<string, boolean>> = {};
    Object.entries(matrix || {}).forEach(([productFamilyId, row]) => {
      const normalizedRow: Record<string, boolean> = {};
      Object.entries(row || {}).forEach(([payer, allowed]) => {
        normalizedRow[normalizePayerName(String(payer))] = (allowed as any) === true;
      });
      out[String(productFamilyId)] = normalizedRow;
    });
    return out;
  }, [drugPayerMatrixByCategory, decisionLogicScenario?.category]);

  // Clears a favorite's active flag through the favorites update endpoint, which hides it (only
  // active favorites are listed), and confirms with the same toast Results uses.
  const removeFavorite = async (fav: FavoriteScenario) => {
    // Demo accounts are read-only server-side (423). Stop before the optimistic change so the
    // list never shows a state the database will not accept.
    if (isDemoMode) {
      toast.current?.show({
        severity: 'info',
        summary: 'Read-only demo',
        detail: 'Favorites cannot be changed from a demo account.',
        life: 3000,
      });
      return;
    }

    setUpdatingFavoriteId(fav.id);
    try {
      // Remove updated_by if present to prevent API issues
      const resultsToUpdate = fav.scenario_details.map(d => {
        const { updated_by, ...rest } = d as any; // eslint-disable-line @typescript-eslint/no-unused-vars
        return { ...rest, is_active: false };
      });

      await ApiService.updateFavoriteScenario({
        user_id: fav.logged_in_user,
        category: fav.category,
        scenario_results: resultsToUpdate
      });

      removeFromScenarioResultsCache(fav.scenario_details);
      notify(toast, `${fav.category} removed from favorites`);

      // Reload favorites to get source of truth from database
      await loadFavorites();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update favorite status',
        life: 3000,
      });
      // Reload on error too to ensure correct state
      await loadFavorites();
    } finally {
      setUpdatingFavoriteId(null);
    }
  };

  const renderRowExpansion = (rowData: FavoriteScenario) => {
    const details: ScenarioResult[] = rowData.scenario_details || [];
    const payerAssignments = getPayerAssignmentsForFavorite(rowData);
    const hasPayerAssignments = !!payerAssignments && Object.keys(payerAssignments).length > 0;
    const colors = colorsFor(rowData.category, details);

    // Same gating as ScenarioResults, but against the rows saved with this favorite:
    // presence decides whether the drill-down link renders, the flag picks the tooltip
    // wording. Families with no contract are saved as placeholder rows, so only a real
    // contract row or a manual adjustment counts.
    const savedRebateDetails: RebateDetail[] = rowData.rebate_details ?? [];
    const pfRebateEarning = new Map<string, boolean>();
    savedRebateDetails.forEach(d => {
      if (!d.has_contract && d.rebate_source !== 'Excluded') return;
      const key = String(d.product_family_id);
      pfRebateEarning.set(key, (pfRebateEarning.get(key) ?? false) || Number(d.rebate_earned || 0) > 0);
    });

    const openRebateDetail = (detail: ScenarioResult, label: string) => {
      setRebateDetailContext({
        favorite: rowData,
        target: {
          scenarioId: Number(detail.scenario_id),
          pfId: String(detail.product_family_id),
          pfLabel: label,
        },
      });
      setRebateDetailVisible(true);
    };

    // Invoice unit strength (default NDC vial strength + UOM, from DisplayUnitEquivalency),
    // shown beside each family when units are invoice units. Oral favorites don't use it.
    const showStrength = unitMode === 'invoice' && !rowData.is_oral;
    const categoryFamilies = productFamiliesByCategory[rowData.category] ?? [];
    const strengthOf = (detail: ScenarioResult) => {
      const name = (detail.product_family_name ?? '').trim().toLowerCase();
      const pf = categoryFamilies.find(f => String(f.product_family_id) === String(detail.product_family_id))
        ?? categoryFamilies.find(f => (f.product_family_name ?? '').trim().toLowerCase() === name);
      return unitStrength(pf?.display_unit_equivalency);
    };

    const renderFamily = (detail: ScenarioResult, label: string) => {
      // Only linked when saved rows exist, so the dialog cannot open onto nothing.
      // "No contracts" vs "nothing saved" are different claims — favorites saved
      // before rebate details were persisted get the second wording.
      const isEarning = pfRebateEarning.get(String(detail.product_family_id));
      // A direct rebate is a column on this row rather than a rebate row, so it can
      // be the only thing the drill-down has to show -- the link opens for it too.
      const hasDirectRebate = Number(detail.total_direct_rebate ?? 0) > 0;
      const hasRebates = isEarning !== undefined || hasDirectRebate;
      const rebateTip = hasRebates
        ? (isEarning || hasDirectRebate
            ? 'View projected rebate performance'
            : 'View potential rebate performance')
        : savedRebateDetails.length > 0
          ? 'No rebate contracts exist for this product family'
          : 'Rebate detail was not saved with this favorite';
      // the app's Tooltip, not a native `title`, so it gets the design-system tooltip styling.
      const rebateTipId = `fav-rebate-tip-${rowData.id}-${detail.product_family_id}`;
      return (
        <span className="results-family-cell inline-flex align-items-center u-gap-1 min-w-0 flex-wrap">
          {hasRebates ? (
            <Button variant="link" id={rebateTipId} data-pr-tooltip={rebateTip} data-pr-position="top" onClick={() => openRebateDetail(detail, label)}>
              {label}
            </Button>
          ) : (
            <span id={rebateTipId} data-pr-tooltip={rebateTip} data-pr-position="top">{label}</span>
          )}
          {showStrength && strengthOf(detail) && <span className="siq-family-strength text-tertiary text-2xs font-medium white-space-nowrap">{strengthOf(detail)}</span>}
          <Tooltip position="top" target={`#${rebateTipId}`} />
        </span>
      );
    };

    const legend = sortByFamily(details.filter(d => Number(d.future_distribution ?? 0) > 0), d => d.product_family_name ?? '');

    return (
      <div className="results-breakdown flex flex-column u-gap-2">
        <div className="results-breakdown-head flex justify-content-between align-items-center u-gap-3 flex-wrap">
          <div className="flex align-items-center u-gap-4 flex-wrap min-w-0">
            <span className="results-breakdown-title text-xs font-semibold">Detailed breakdown</span>
            <div className="siq-legend flex u-gap-3 flex-wrap text-2xs text-secondary">
              {legend.map(d => (
                <span key={d.product_family_id} className="siq-legend-item inline-flex align-items-center u-gap-1 white-space-nowrap">
                  <i className="siq-swatch" style={{ background: familyColor(colors, d.product_family_name ?? '') }} />
                  {familyLabel(d.product_family_name)}
                </span>
              ))}
            </div>
          </div>
          {hasPayerAssignments && (
            <div className="results-breakdown-actions flex u-gap-2 flex-wrap">
              <Button variant="primary-outline" onClick={() => openDecisionLogicModal(rowData)}><i className="pi pi-eye" aria-hidden="true" />View payer assignments</Button>
            </div>
          )}
        </div>
        <BreakdownTable rows={details} metric={ncrMode} unitMode={unitMode} ncrLabel={ncrLabel} renderFamily={renderFamily} familyNote={showStrength ? 'Invoice unit strength' : undefined} />
      </div>
    );
  };

  const renderDecisionLogicContent = (scenario: NonNullable<typeof decisionLogicScenario>) => {
    const details = scenario.details || [];
    const categoryKey = scenario.category || details?.[0]?.category;
    const allFamiliesForCategory = categoryKey ? (productFamiliesByCategory[categoryKey] ?? []) : [];
    const colors = colorsFor(categoryKey, details);

    const productFamilyNameById = allFamiliesForCategory
      .reduce((acc, pf) => {
        const id = pf.product_family_id;
        const name = pf.product_family_name;
        if (id !== null && id !== undefined && name) {
          acc[String(id)] = String(name);
        }
        return acc;
      }, {} as Record<string, string>);

    details.forEach((row) => {
      const id = row.product_family_id;
      const name = row.product_family_name;
      if (id !== null && id !== undefined && name) {
        productFamilyNameById[String(id)] = String(name);
      }
    });

    const payerRows = payerAssignmentsToRows(scenario.payerAssignments, productFamilyNameById, payerMixByName);
    const totalAllocatedPercent = payerRows.reduce((sum, r) => {
      const pct = typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent) ? r.payerPercent : 0;
      return sum + pct;
    }, 0);
    const hasAnyAllocatedPercent = payerRows.some((r) => typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent));

    const payerBreakdownProductFamilies: Array<{ productFamilyId: string; productFamilyName: string }> =
      allFamiliesForCategory.length > 0
        ? allFamiliesForCategory
            .filter((pf) => pf.product_family_id !== null && pf.product_family_id !== undefined)
            .map((pf) => ({
              productFamilyId: String(pf.product_family_id),
              productFamilyName: String(pf.product_family_name ?? ''),
            }))
        : details
            .reduce((acc, d) => {
              const id = String(d.product_family_id);
              if (!acc.some((x) => x.productFamilyId === id)) {
                acc.push({ productFamilyId: id, productFamilyName: String(d.product_family_name || '') });
              }
              return acc;
            }, [] as Array<{ productFamilyId: string; productFamilyName: string }>);

    // Payers in the mix with no assignment, which is why the total can be under 100%: excluded (payer
    // preferences block them on every product family) or too small for the generator to assign individually.
    const assignedPayers = new Set(payerRows.map((r) => normalizePayerName(String(r.payer))));
    const unassignedPayers = modalPayerMix
      .filter((p) => p && p.payer && Number(p.percent) > 0 && !assignedPayers.has(normalizePayerName(String(p.payer))))
      .map((p) => ({ payer: String(p.payer), percent: Number(p.percent) }));
    const isExcludedPayer = (payer: string) => payerBreakdownProductFamilies.length > 0 && payerBreakdownProductFamilies.every(
      (pf) => drugPayerMatrixByNormalizedPayer?.[pf.productFamilyId]?.[normalizePayerName(payer)] === false,
    );
    const excludedUnassigned = unassignedPayers.filter((p) => isExcludedPayer(p.payer));
    const tooSmallUnassigned = unassignedPayers.filter((p) => !isExcludedPayer(p.payer));

    const expandedForScenario = expandedDecisionPayers[scenario.scenario_id] ?? {};

    const toggleExpandedPayer = (payer: string) => {
      setExpandedDecisionPayers((prev) => {
        const current = prev[scenario.scenario_id] ?? {};
        const isOpen = current[payer] === true;
        const nextForScenario = isOpen ? {} : { [payer]: true };
        return { ...prev, [scenario.scenario_id]: nextForScenario };
      });
    };

    const payerRowExpansion = (payer: string) => {
      if (payerPrefsLoading || productFamiliesLoading) return <div className="pa-expansion flex flex-column u-gap-2 u-pl-10 text-xs text-tertiary">Loading…</div>;
      if (payerPrefsError) return <div className="pa-expansion flex flex-column u-gap-2 u-pl-10 text-xs text-tertiary">{payerPrefsError}</div>;
      if (productFamiliesError) return <div className="pa-expansion flex flex-column u-gap-2 u-pl-10 text-xs text-tertiary">{productFamiliesError}</div>;
      if (payerBreakdownProductFamilies.length === 0) {
        return <div className="pa-expansion flex flex-column u-gap-2 u-pl-10 text-xs text-tertiary">No product families found for this scenario.</div>;
      }
      return (
        <PayerEligibility
          items={sortByFamily(payerBreakdownProductFamilies, (r) => r.productFamilyName).map((r) => {
            const rawName = String(r.productFamilyName || '');
            return {
              id: String(r.productFamilyId),
              label: rawName ? formatPfLabel(rawName) : '—',
              color: rawName ? familyColor(colors, rawName) : 'var(--siq-icon-faint)',
              allowed: (drugPayerMatrixByNormalizedPayer?.[String(r.productFamilyId)]?.[normalizePayerName(payer)] ?? true) === true,
            };
          })}
        />
      );
    };

    if (payerRows.length === 0) return <span className="siq-dim text-tertiary">—</span>;

    return (
      <PayerAssignmentTable
        rows={payerRows}
        expandedPayer={Object.keys(expandedForScenario).find((payer) => expandedForScenario[payer]) ?? null}
        onToggle={toggleExpandedPayer}
        renderExpansion={payerRowExpansion}
        renderFamily={(r) => {
          const raw = (r.productFamilyName && r.productFamilyName !== r.productFamilyId) ? String(r.productFamilyName) : '';
          return raw
            ? <span className="pa-family inline-flex align-items-center u-gap-2 min-w-0"><i className="siq-swatch" style={{ background: familyColor(colors, raw) }} />{formatPfLabel(raw)}</span>
            : <span>—</span>;
        }}
        totalShare={hasAnyAllocatedPercent ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(totalAllocatedPercent) + '%' : '—'}
        tooSmall={tooSmallUnassigned}
        excluded={excludedUnassigned}
      />
    );
  };

  const favoritesTable = useReactTable({
    data: rows,
    columns: favoriteColumns,
    meta: { view: { metricName, isDemoMode, updatingFavoriteId, removeFavorite: (fav) => { void removeFavorite(fav); } } satisfies FavoritesView },
    state: { sorting, expanded: expandedRows as ExpandedState },
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    onExpandedChange: (updater) => {
      const next = typeof updater === 'function' ? updater(expandedRows as ExpandedState) : updater;
      setExpandedRows(next === true ? Object.fromEntries(rows.map((r) => [r.id, true])) : (next as Record<number, boolean>));
    },
    getRowId: (row) => String(row.id),
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageIndex: 0, pageSize: FAVORITES_PAGE_SIZE } },
  });
  const sortLine = sortLineFor(sorting, sortLabels);
  // Narrow screens scroll the table sideways; the controls show only when it overflows.
  const horizontalScroll = useAppTableHorizontalScroll();

  const exportItems = [
    { label: 'Export as CSV', icon: 'pi pi-file', command: () => handleExport('csv', exportableData, exportableRebateDetails, inventoryMode === 'oral') },
    { label: 'Export as Excel', icon: 'pi pi-file-excel', command: () => handleExport('excel', exportableData, exportableRebateDetails, inventoryMode === 'oral') },
  ];

  const savedCount = modeFavorites.length;

  return (
    <div className="favorite-scenarios-container flex flex-column u-gap-4 min-w-0">
      <PageHeader
        title="Favorite scenarios"
        subtitle={`${savedCount} saved scenario${savedCount === 1 ? '' : 's'} for this practice`}
        actions={<>
          <Menu model={exportItems} popup popupAlignment="right" ref={exportMenuRef} />
          <Button onClick={(e) => exportMenuRef.current?.toggle(e)} disabled={isExporting || exportableData.length === 0} aria-haspopup="menu"><i className="pi pi-download" aria-hidden="true" />Export<i className="pi pi-angle-down" aria-hidden="true" /></Button>
        </>}
      />

      <FilterBand>
        {hasOral && hasNonOral && (
          <Field label="Inventory type">
            <SegmentedControl
              aria-label="Inventory type"
              value={inventoryMode}
              onValueChange={(next) => {
                const value = next === 'oral' ? 'oral' : 'nonoral';
                setInventoryMode(value);
                clearFilters();
                if (value === 'oral') setNcrMode('medicare');
              }}
              options={[{ value: 'nonoral', label: 'Non-oral' }, { value: 'oral', label: 'Oral' }]}
            />
          </Field>
        )}
        <Field label="Created by" htmlFor="fav-user">
          <Dropdown
            inputId="fav-user"
            value={filterUser || ALL}
            options={[{ label: 'All users', value: ALL }, ...userOptions.map(u => ({ label: u, value: u }))]}
            onChange={(e) => setFilterUser(e.value === ALL ? '' : e.value ?? '')}
            placeholder="All users"
            className="w-10rem"
            disabled={userOptions.length === 0 || isDemoMode}
          />
        </Field>
        <Field label="Therapeutic area" htmlFor="fav-area">
          <Dropdown
            inputId="fav-area"
            value={filterCategory || ALL}
            options={[{ label: 'All areas', value: ALL }, ...categoryFilterOptions.map(c => ({ label: c, value: c }))]}
            onChange={(e) => setFilterCategory(e.value === ALL ? '' : e.value ?? '')}
            placeholder="All areas"
            className="w-10rem"
            disabled={categoryFilterOptions.length === 0}
          />
        </Field>
        {payerMixLoaded && (payerMixAvailable || inventoryMode === 'oral') && (
          <Field label="Metric" htmlFor="fav-metric">
            <Dropdown
              inputId="fav-metric"
              value={ncrMode}
              options={[
                { label: `${ncrLabel} NCR`, value: 'medicare' },
                { label: 'Weighted NCR', value: 'weighted', disabled: inventoryMode === 'oral' },
              ]}
              optionDisabled="disabled"
              onChange={(e) => setNcrMode(e.value)}
              className="w-10rem"
            />
          </Field>
        )}
        {payerMixLoaded && favorites.length > 0 && (
          <Field label="Units" htmlFor="fav-units">
            <Dropdown
              inputId="fav-units"
              value={unitMode}
              options={[{ label: 'Category units', value: 'category' }, { label: 'Invoice units', value: 'invoice' }]}
              onChange={(e) => setUnitMode(e.value)}
              className="w-10rem"
            />
          </Field>
        )}
        <Button variant="primary-text" className="align-self-end" onClick={clearFilters} disabled={!filtersActive}>
          Clear filters
        </Button>
      </FilterBand>

      <Card className="fav-card">
        <AppTable
          isBordered={false}
          size="sm"
          ariaLabel="Favorite scenarios"
          table={favoritesTable}
          // Columns size to their content (headers like "Weighted reimb." need the room); nothing is pinned.
          tableLayout="auto"
          horizontalScroll={horizontalScroll}
          headerSlot={horizontalScroll.isOverflowingHorizontally ? <AppTableHorizontalScrollControls horizontalScroll={horizontalScroll} /> : undefined}
          isLoading={loading && rows.length === 0}
          loadingSkeletonRowCount={5}
          emptyState={modeFavorites.length > 0 ? (
            <div className="fav-empty flex flex-column align-items-center u-gap-2 u-py-7 u-px-4 text-xs text-tertiary text-center">
              <span>No favorites match these filters.</span>
              <Button variant="primary-text" onClick={clearFilters}>Clear filters</Button>
            </div>
          ) : (
            <div className="fav-empty flex flex-column align-items-center u-gap-2 u-py-7 u-px-4 text-xs text-tertiary text-center">No favorite scenarios saved for this practice yet. Star a scenario on Results to keep it here.</div>
          )}
          onCellClick={({ row, columnId }) => { if (columnId !== 'controls') row.toggleExpanded(); }}
          rowExpansion={{ renderSubRow: (row) => <div className="results-expansion ">{renderRowExpansion(row.original)}</div> }}
          pagination={rows.length > 0 ? { rowsPerPageOptions: [10, 25, 50] } : false}
          footerSlot={rows.length > 0 && sortLine ? <span className="siq-dim text-tertiary">{sortLine}</span> : undefined}
          messages={{ paginationRangeText: ({ first, last, total }) => `Showing ${first}–${last} of ${total} favorite${total === 1 ? '' : 's'}` }}
        />
      </Card>

      <Modal
        visible={isDecisionLogicModalVisible}
        onHide={closeDecisionLogicModal}
        onExited={() => setDecisionLogicScenario(null)}
        title={`Payer assignments · ${decisionLogicScenario?.category ?? ''}`}
        subtitle="Payer optimized · each payer is assigned its most profitable product family"
        width="51.4062rem"
        dismissableMask
        footer={<Button variant="primary-outline" onClick={closeDecisionLogicModal}>Close</Button>}
      >
        {decisionLogicScenario ? renderDecisionLogicContent(decisionLogicScenario) : null}
      </Modal>

      <RebateDetailDialog
        visible={rebateDetailVisible}
        onHide={() => setRebateDetailVisible(false)}
        onExited={() => setRebateDetailContext(null)}
        target={rebateDetailContext?.target ?? null}
        rebateDetailRows={rebateDetailRows}
        summaryRow={rebateDetailSummaryRow}
        // A favorite's scenario_id is an artifact of the run it was saved from, so the
        // category names the context instead of 'Scenario N'.
        scenarioLabel={rebateDetailContext?.favorite.category}
      />
    </div>
  );
};

export default FavoriteScenarios;
